import type { AuditLogsQueryParams, AuditLogsResponse } from '../../types/audit-logs'
import { apiClient } from '../api/apiClient'
import { logger } from '../../utils/logger'

export const auditLogsService = {
  /**
   * Fetch audit logs from the backend
   * Uses existing apiClient with automatic token refresh and error handling
   */
  async fetchAuditLogs(params: AuditLogsQueryParams = {}): Promise<AuditLogsResponse> {
    try {
      logger.log('Fetching audit logs with params:', params)
      
      // Build query parameters, filtering out undefined values
      const queryParams: Record<string, string> = {}
      if (params.resourceType) queryParams.resourceType = params.resourceType
      if (params.resourceId) queryParams.resourceId = params.resourceId
      if (params.userId) queryParams.userId = params.userId
      if (params.startDate) queryParams.startDate = params.startDate
      if (params.endDate) queryParams.endDate = params.endDate
      if (params.limit) queryParams.limit = params.limit.toString()
      if (params.cursor) queryParams.cursor = params.cursor

      const response = await apiClient.get<any>('/platform-audit-logs/logs', queryParams)
      
      // Log the full response structure to debug
      logger.log('Full API response:', response)
      logger.log('Response data type:', typeof response.data)
      logger.log('Response data:', response.data)
      
      // The API returns { logs: [...], pagination: {...} } structure
      // We need to map it to our expected { data: [...], pagination: {...} } structure
      const auditLogsData = response.data?.logs || response.logs || []
      const paginationData = response.data?.pagination || response.pagination || {}
      
      logger.log('Audit logs fetched successfully, count:', Array.isArray(auditLogsData) ? auditLogsData.length : 0)
      
      // Log the structure of the first audit log to understand the data format
      if (Array.isArray(auditLogsData) && auditLogsData.length > 0) {
        logger.log('First audit log structure:', JSON.stringify(auditLogsData[0], null, 2))
        logger.log('First audit log keys:', Object.keys(auditLogsData[0]))
      }
      
      // Return the response in the expected format
      return {
        data: Array.isArray(auditLogsData) ? auditLogsData : [],
        pagination: {
          total: paginationData.total || (Array.isArray(auditLogsData) ? auditLogsData.length : 0),
          hasMore: paginationData.hasMore || false,
          nextCursor: paginationData.nextCursor || undefined
        }
      }
    } catch (error) {
      logger.error('Failed to fetch audit logs:', error)
      throw new Error('Failed to fetch audit logs. Please try again or contact the platform team.')
    }
  },

  /**
   * Format timestamp for display
   */
  formatTimestamp(timestamp: string): string {
    try {
      const date = new Date(timestamp)
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short'
      })
    } catch (error) {
      logger.error('Failed to format timestamp:', error)
      return timestamp
    }
  },

  /**
   * Format HTTP method for display
   */
  formatHttpMethod(method: string): string {
    return method.toUpperCase()
  },

  /**
   * Format change value for display
   */
  formatChangeValue(value: unknown): string {
    if (value === null || value === undefined) {
      return 'null'
    }
    if (typeof value === 'string') {
      return value
    }
    if (typeof value === 'boolean') {
      return value.toString()
    }
    if (typeof value === 'number') {
      return value.toString()
    }
    if (typeof value === 'object') {
      try {
        return JSON.stringify(value)
      } catch {
        return '[object Object]'
      }
    }
    try {
      return String(value)
    } catch {
      return '[object Object]'
    }
  },

  /**
   * Get HTTP method color for UI styling
   */
  getHttpMethodColor(method: string): string {
    const colors: Record<string, string> = {
      GET: 'text-blue-600',
      POST: 'text-green-600',
      PUT: 'text-yellow-600',
      DELETE: 'text-red-600',
      PATCH: 'text-purple-600'
    }
    return colors[method.toUpperCase()] || 'text-gray-600'
  }
}
